import React, { useState } from 'react'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { useTranslation } from 'react-i18next'
import ConfirmDialog, {
  ConfirmDialogDetail,
} from '../containers/ConfirmDialog'
import './detail/detail.css'
import '../../i18n'
import { applyPlaceholders } from '../../utils/applyPlaceholders'

export type DeleteItemMeta = {
  /** Entity type label, e.g. "Pipeline", "Task" */
  entityType?: string
  /** Primary display name of the item */
  name?: string
  /** Extra identifying rows shown in the confirm dialog */
  details?: ConfirmDialogDetail[]
}

interface DeleteButtonProps {
  onDelete: () => void
  item?: DeleteItemMeta
  className?: string
}

const DeleteButton: React.FC<DeleteButtonProps> = ({
  onDelete,
  item,
  className = '',
}) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  const handleDelete = () => {
    onDelete()
    setOpen(false)
  }

  const entity =
    item?.entityType ||
    t('deleteConfirm.item', { defaultValue: 'item' })
  const title = t('deleteConfirm.title', {
    entity,
    defaultValue: `Delete ${entity}`,
  })
  const named = Boolean(item?.name)
  const rawMessage = named
    ? t('deleteConfirm.messageNamed', {
        name: item?.name,
        entity,
        defaultValue: `Are you sure you want to delete the ${entity} “${item?.name}”? This action cannot be undone.`,
      })
    : t('deleteConfirm.message', {
        entity,
        defaultValue: `Are you sure you want to delete this ${entity}? This action cannot be undone.`,
      })
  const message = applyPlaceholders(rawMessage, {
    name: item?.name || '',
    entity,
  })

  const details: ConfirmDialogDetail[] = []
  if (item?.name) {
    details.push({
      label: t('deleteConfirm.fields.name', { defaultValue: 'Name' }),
      value: item.name,
    })
  }
  if (item?.entityType) {
    details.push({
      label: t('deleteConfirm.fields.type', { defaultValue: 'Type' }),
      value: item.entityType,
    })
  }
  if (item?.details?.length) {
    const idLabel = t('deleteConfirm.fields.id', { defaultValue: 'ID' })
      .trim()
      .toLowerCase()
    details.push(
      ...item.details.filter((d) => {
        const label = String(d.label ?? '')
          .trim()
          .toLowerCase()
        return label !== idLabel && label !== 'id'
      })
    )
  }

  return (
    <>
      <button
        type="button"
        className={`detail-icon-btn detail-icon-btn--danger ${className}`.trim()}
        title={t('Delete')}
        aria-label={t('Confirm Delete')}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
      </button>

      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleDelete}
        tone="danger"
        title={title}
        message={message}
        details={details.length ? details : undefined}
        confirmText={t('Delete')}
        cancelText={t('Cancel')}
      />
    </>
  )
}

export default DeleteButton
